/**
 * Google Analytics 4 Event Tracking Utility
 *
 * This utility provides helper functions to track user interactions
 * throughout the Zykain website.
 */

/**
 * Track navigation clicks
 * @param {string} section - The section being navigated to (e.g., 'home', 'services', 'contact')
 * @param {string} source - Where the click originated (e.g., 'navbar', 'mobile_menu', 'footer')
 */
export const trackNavigation = (section, source = 'navbar') => {
  if (window.gtag) {
    window.gtag('event', 'navigation_click', {
      event_category: 'Navigation',
      event_label: section,
      navigation_source: source,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track service card interactions
 * @param {string} serviceName - The name of the service
 * @param {string} action - The action taken (e.g., 'get_started_click', 'card_hover')
 */
export const trackServiceInteraction = (serviceName, action = 'get_started_click') => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: 'Service',
      event_label: serviceName,
      service_name: serviceName,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track contact form events
 * @param {string} action - The form action (e.g., 'submit', 'field_focus', 'validation_error')
 * @param {object} data - Additional data about the form interaction
 */
export const trackFormInteraction = (action, data = {}) => {
  if (window.gtag) {
    window.gtag('event', `form_${action}`, {
      event_category: 'Contact Form',
      event_label: action,
      ...data,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track FAQ accordion interactions
 * @param {string} question - The FAQ question being expanded/collapsed
 * @param {boolean} isExpanding - True if expanding, false if collapsing
 */
export const trackFAQInteraction = (question, isExpanding) => {
  if (window.gtag) {
    window.gtag('event', 'faq_interaction', {
      event_category: 'FAQ',
      event_label: question,
      action: isExpanding ? 'expand' : 'collapse',
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track slider/carousel interactions
 * @param {string} action - The slider action (e.g., 'next', 'prev', 'dot_click', 'auto_scroll')
 * @param {number} slideIndex - The index of the slide navigated to
 * @param {string} slideTitle - The title of the slide
 */
export const trackSliderInteraction = (action, slideIndex, slideTitle) => {
  if (window.gtag) {
    window.gtag('event', 'slider_interaction', {
      event_category: 'Slider',
      event_label: slideTitle,
      action: action,
      slide_index: slideIndex,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track external link clicks
 * @param {string} url - The external URL being clicked
 * @param {string} linkName - A friendly name for the link (e.g., 'LinkedIn', 'Twitter')
 */
export const trackExternalLink = (url, linkName) => {
  if (window.gtag) {
    window.gtag('event', 'external_link_click', {
      event_category: 'External Links',
      event_label: linkName,
      link_url: url,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track section visibility (when user scrolls to a section)
 * @param {string} sectionName - The name of the section that became visible
 */
export const trackSectionView = (sectionName) => {
  if (window.gtag) {
    window.gtag('event', 'section_view', {
      event_category: 'Engagement',
      event_label: sectionName,
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track CTA button clicks
 * @param {string} ctaText - The text of the CTA button
 * @param {string} ctaLocation - Where the CTA is located on the page
 */
export const trackCTAClick = (ctaText, ctaLocation) => {
  if (window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'CTA',
      event_label: ctaText,
      cta_location: ctaLocation,
      timestamp: new Date().toISOString()
    });
  }
};
